import { useMemo } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button } from '@components/ui/Button';
import { Input } from '@components/ui/Input';
import { useGoogleAuth } from '@features/auth/hooks/useGoogleAuth';

import { useBusinessSettingsStore } from '../store/businessSettingsStore';
import type { AccountData } from '../types';

interface Props {
  onNext: () => void;
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

function ArrowRight() {
  return (
    <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4" aria-hidden="true">
      <path
        d="M3 8h10M9 4l4 4-4 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function Step1CreateAccount({ onNext }: Props) {
  const { t } = useTranslation();
  const setAccount = useBusinessSettingsStore((s) => s.setAccount);

  const { triggerGoogleAuth, isPending: isGooglePending, error: googleError } = useGoogleAuth({
    mode: 'register',
    onRegisterSuccess: (user, accountId) => {
      setAccount({
        authMethod: 'google',
        fullName: `${user.firstName} ${user.lastName}`.trim(),
        email: user.email,
        accountId,
      });
      onNext();
    },
  });

  const schema = useMemo(
    () =>
      z
        .object({
          fullName: z.string().min(2, t('onboarding.step1.fullNameRequired')),
          email: z.string().email(t('onboarding.step1.emailInvalid')),
          password: z.string().min(8, t('onboarding.step1.passwordTooShort')),
          confirmPassword: z.string(),
        })
        .refine((d) => d.password === d.confirmPassword, {
          message: t('onboarding.step1.passwordMismatch'),
          path: ['confirmPassword'],
        }),
    [t],
  );

  type FormValues = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  function onSubmit(values: FormValues) {
    const data: AccountData = {
      authMethod: 'email',
      fullName: values.fullName,
      email: values.email,
      password: values.password,
    };
    setAccount(data);
    onNext();
  }

  return (
    <div>
      <h2 className="font-display text-[2rem] font-semibold leading-tight text-foreground">
        {t('onboarding.step1.title')}
      </h2>
      <p className="mt-2 text-sm text-muted-foreground">{t('onboarding.step1.subtitle')}</p>

      <div className="mt-8 space-y-5">
        {googleError && (
          <div role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {googleError}
          </div>
        )}

        <Button
          type="button"
          variant="secondary"
          size="lg"
          className="w-full justify-center"
          leftIcon={<GoogleIcon />}
          onClick={() => triggerGoogleAuth()}
          isLoading={isGooglePending}
        >
          {t('onboarding.step1.continueWithGoogle')}
        </Button>

        <div className="flex items-center gap-3">
          <div className="h-px flex-1 bg-border" />
          <span className="text-xs font-medium text-muted-foreground">
            {t('onboarding.step1.orSeparator')}
          </span>
          <div className="h-px flex-1 bg-border" />
        </div>

        <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
          <Input
            label={t('onboarding.step1.fullName')}
            placeholder={t('onboarding.step1.fullNamePlaceholder')}
            autoComplete="name"
            error={errors.fullName?.message}
            {...register('fullName')}
          />

          <Input
            type="email"
            label={t('onboarding.step1.email')}
            placeholder={t('onboarding.step1.emailPlaceholder')}
            autoComplete="email"
            error={errors.email?.message}
            {...register('email')}
          />

          <div className="grid grid-cols-2 gap-3">
            <Input
              type="password"
              label={t('onboarding.step1.password')}
              placeholder={t('onboarding.step1.passwordPlaceholder')}
              autoComplete="new-password"
              error={errors.password?.message}
              {...register('password')}
            />
            <Input
              type="password"
              label={t('onboarding.step1.confirmPassword')}
              placeholder={t('onboarding.step1.confirmPasswordPlaceholder')}
              autoComplete="new-password"
              error={errors.confirmPassword?.message}
              {...register('confirmPassword')}
            />
          </div>

          <p className="text-xs leading-relaxed text-muted-foreground">
            {t('onboarding.step1.terms')}{' '}
            <a href="/terms" className="underline underline-offset-2 hover:text-foreground">
              {t('onboarding.step1.termsLink')}
            </a>{' '}
            {t('onboarding.step1.and')}{' '}
            <a href="/privacy" className="underline underline-offset-2 hover:text-foreground">
              {t('onboarding.step1.privacyLink')}
            </a>
            .
          </p>

          <div className="flex justify-end pt-2">
            <Button type="submit" size="lg" rightIcon={<ArrowRight />}>
              {t('onboarding.continue')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
